import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import Button from '../../components/Button';
import Table from '../../components/Table';

import {
  DEFAULT_BENCHMARK_SORT,
  defaultSortDirection,
  dropTone,
  formatDrop,
  materialTokenChange,
  partitionBenchmarkOutcomes,
  type BenchmarkDropTone,
  type BenchmarkSortColumn,
  type IBenchmarkSort,
} from './assistantBenchmarkView';
import BenchmarkTranscriptModal from './BenchmarkTranscriptModal';
import {
  budgetSummary,
  budgetVerdict,
  formatTokens,
  systemPromptTable,
  toolTable,
  type BudgetVerdictTone,
  type DeltaTone,
  type IMetricRow,
} from './promptBudgetView';

import type {
  IMopsAssistantBenchmarkArtifactV1,
  IMopsAssistantBenchmarkScenarioOutcomeV1,
  IMopsAssistantBenchmarkScenarioTranscriptsV1,
  MopsAssistantBenchmarkDecision,
} from '@orn/global/assistant/MopsAssistantBenchmarkArtifact.js';
import type { IMopsAssistantBenchmarkPromptBudgetV1 } from '@orn/global/assistant/promptBudget.js';

interface IVerdictColumn {
  column: BenchmarkSortColumn;
  label: string;
  title: string;
  description: string;
}

/** The verdict table's columns, in render order; every one orders the table. */
const VERDICT_COLUMNS: IVerdictColumn[] = [
  {
    column: 'scenario',
    label: 'Scenario',
    title: 'Scenario',
    description: 'The eval scenario compared, by id. Hover a row for its one-line rationale.',
  },
  {
    column: 'decision',
    label: 'Decision',
    title: 'Decision',
    description:
      "The ladder's verdict: accept (not worse), fail (confidently and meaningfully worse), main-unstable (baseline too flaky to gate), or inconclusive-error (couldn't be judged).",
  },
  {
    column: 'drop',
    label: 'Drop',
    title: 'Drop',
    description: 'Base pass-rate minus head pass-rate. Positive means head passed less often — a regression.',
  },
  {
    column: 'base',
    label: 'Base',
    title: 'Base',
    description: 'Baseline commit — whole-run passes / measured runs.',
  },
  {
    column: 'head',
    label: 'Head',
    title: 'Head',
    description: 'Candidate commit — whole-run passes / measured runs.',
  },
  {
    column: 'tokens',
    label: 'Tokens',
    title: 'Tokens',
    description:
      'Token cost the candidate added over the baseline, where the increase was material — big enough to matter and unlikely to be luck. `out` is output tokens, `rsn` the reasoning subset of them. Hover a value for the medians behind it. Advisory: token cost never gates the run.',
  },
];

interface ISectionLabelProps {
  children: React.ReactNode;
}

/** A dense uppercase section label inside the detail pane. */
const SectionLabel = ({ children }: ISectionLabelProps) => (
  <span className="text-fg-tertiary text-[11px] font-medium uppercase tracking-wider">{children}</span>
);

// More tokens (increase) is a regression → error; fewer → success; no change / no
// base → muted. Mirrors the PR comment's ⚠️/🟢/— but in MOPS theme tokens.
const DELTA_TONE_CLASS: Record<DeltaTone, string> = {
  increase: 'text-fg-error-primary',
  decrease: 'text-fg-success-primary',
  flat: 'text-fg-tertiary',
  none: 'text-fg-tertiary',
};

const BUDGET_VERDICT_TONE_CLASS: Record<BudgetVerdictTone, string> = {
  ok: 'text-fg-success-primary',
  warn: 'text-fg-warning-primary',
  over: 'text-fg-error-primary',
  neutral: 'text-fg-secondary',
};

interface IBudgetRowProps {
  row: IMetricRow;
}

/** One `label | Base | Head | Δ` row of a prompt-budget table. */
const BudgetRow = ({ row }: IBudgetRowProps) => (
  <Table.Row>
    <Table.Cell
      className={cx(row.indent && 'pl-6', row.emphasis && 'font-semibold')}
      mono={row.indent}
      muted={row.indent}
    >
      {row.label}
    </Table.Cell>
    <Table.Cell mono muted className="text-right">
      {row.base === null ? '—' : formatTokens(row.base)}
    </Table.Cell>
    <Table.Cell mono className={cx('text-right', row.emphasis && 'font-semibold')}>
      {formatTokens(row.head)}
    </Table.Cell>
    <Table.Cell mono className={cx('text-right', DELTA_TONE_CLASS[row.delta.tone])}>
      {row.delta.label}
    </Table.Cell>
  </Table.Row>
);

interface IBudgetTableProps {
  /** Header for the first column — "Component" for the prompt, "Metric" for tools. */
  firstColumn: string;
  rows: IMetricRow[];
}

const BudgetTable = ({ firstColumn, rows }: IBudgetTableProps) => (
  <Table>
    <Table.Header>
      <Table.Row>
        <Table.HeaderCell>{firstColumn}</Table.HeaderCell>
        <Table.HeaderCell className="text-right">Base</Table.HeaderCell>
        <Table.HeaderCell className="text-right">Head</Table.HeaderCell>
        <Table.HeaderCell className="text-right">Δ</Table.HeaderCell>
      </Table.Row>
    </Table.Header>
    <Table.Body>
      {rows.map((row) => (
        <BudgetRow key={row.label} row={row} />
      ))}
    </Table.Body>
  </Table>
);

interface IPromptBudgetViewProps {
  promptBudget: IMopsAssistantBenchmarkPromptBudgetV1 | null;
}

/**
 * The static-prefix (system prompt + tool registry) budget for a run, base vs
 * head — the mops mirror of the PR comment's prompt-budget benchmark. Renders a
 * headline verdict, a per-module System-prompt table, and a Tool-registry table.
 * `null` when the run couldn't be measured (the head commit predates the measure
 * code, or measuring it failed) — degrades to a one-line note.
 */
const PromptBudgetView = ({ promptBudget }: IPromptBudgetViewProps) => {
  if (promptBudget === null) {
    return (
      <div className="border-border-primary flex flex-col gap-1 border-t pt-3">
        <SectionLabel>Prompt budget</SectionLabel>
        <span className="text-fg-tertiary text-[11px] italic">
          Unavailable for this run — the head commit predates the measure, or measuring it failed.
        </span>
      </div>
    );
  }

  const { base, head } = promptBudget;
  const summary = budgetSummary(base, head);
  const verdict = budgetVerdict(base, head);
  const sys = systemPromptTable(base, head);
  const tools = toolTable(base, head);

  const sysRows = [sys.totalRow, ...sys.moduleRows];
  const toolRows = [tools.totalRow, tools.countRow, tools.descriptionsRow, tools.parametersRow, ...tools.topParamRows];

  return (
    <div className="border-border-primary flex flex-col gap-3 border-t pt-3">
      <div className="flex flex-col gap-1">
        <SectionLabel>Prompt budget · static prefix</SectionLabel>
        <span className={cx('text-xs font-medium', BUDGET_VERDICT_TONE_CLASS[verdict.tone])}>{verdict.text}</span>
        <span className="text-fg-tertiary text-[11px]">
          <span className="font-mono">{summary.model}</span>
          {' · '}
          {summary.windowKnown
            ? `${formatTokens(summary.combinedTokens)} / ${formatTokens(summary.budgetTokens ?? 0)} tok soft target · ${summary.pctOfContext?.toFixed(1)}% of context`
            : `${formatTokens(summary.combinedTokens)} tok · context window unknown`}
          {` · ${summary.estimator} approx (deltas exact)`}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <SectionLabel>System prompt</SectionLabel>
        <BudgetTable firstColumn="Component" rows={sysRows} />
        {sys.zeroModules.length > 0 && (
          <span className="text-fg-tertiary text-[11px] italic">Empty on both: {sys.zeroModules.join(' · ')}</span>
        )}
        <span className="text-fg-tertiary text-[11px]">
          By channel:{' '}
          {sys.channelBits.map((c, i) => (
            <React.Fragment key={c.channel}>
              {i > 0 && ' · '}
              <span className="font-mono">{c.channel}</span> {formatTokens(c.tokens)}
              {(c.delta.tone === 'increase' || c.delta.tone === 'decrease') && (
                <span className={DELTA_TONE_CLASS[c.delta.tone]}> ({c.delta.label})</span>
              )}
            </React.Fragment>
          ))}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <SectionLabel>Tool registry · {formatTokens(head.tools.toolCount)} tools</SectionLabel>
        <BudgetTable firstColumn="Metric" rows={toolRows} />
      </div>
    </div>
  );
};

/**
 * The scenario's captured transcripts if at least one arm holds a run, else null
 * — so a verdict row is made clickable only when there's actually a transcript to
 * open (capture is dormant, so today this is null for every run).
 */
const scenarioTranscripts = (
  transcripts: IMopsAssistantBenchmarkScenarioTranscriptsV1 | null | undefined
): IMopsAssistantBenchmarkScenarioTranscriptsV1 | null =>
  transcripts != null && ((transcripts.base?.length ?? 0) > 0 || (transcripts.head?.length ?? 0) > 0)
    ? transcripts
    : null;

interface ITranscriptScenario {
  scenarioId: string;
  transcripts: IMopsAssistantBenchmarkScenarioTranscriptsV1;
}

// A positive drop is head passing less often, so bad reads error and good reads
// success — the same direction the prompt-budget delta column uses.
const DROP_TONE_CLASS: Record<BenchmarkDropTone, string> = {
  regression: 'text-fg-error-primary',
  improvement: 'text-fg-success-primary',
  neutral: 'text-fg-tertiary',
};

const DECISION_TONE_CLASS: Record<MopsAssistantBenchmarkDecision, string> = {
  fail: 'text-fg-error-primary',
  accept: 'text-fg-success-primary',
  'main-unstable': 'text-fg-warning-primary',
  'inconclusive-error': 'text-fg-warning-primary',
};

interface IOutcomeRowProps {
  outcome: IMopsAssistantBenchmarkScenarioOutcomeV1;
  /** Opens the base-vs-head transcript modal; only wired when the scenario carries a capture. */
  onOpen: (scenario: ITranscriptScenario) => void;
}

/** One scenario's verdict row, clickable when the scenario captured transcripts. */
const OutcomeRow = ({ outcome, onOpen }: IOutcomeRowProps) => {
  const transcripts = scenarioTranscripts(outcome.transcripts);
  const tokenChange = materialTokenChange(outcome.perf);
  const openTranscripts = () => {
    if (transcripts !== null) {
      onOpen({ scenarioId: outcome.scenarioId, transcripts });
    }
  };
  return (
    <Table.Row
      onClick={transcripts !== null ? openTranscripts : undefined}
      tabIndex={transcripts !== null ? 0 : undefined}
      role={transcripts !== null ? 'button' : undefined}
      aria-label={transcripts !== null ? `View transcripts for ${outcome.scenarioId}` : undefined}
      onKeyDown={
        transcripts !== null
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openTranscripts();
              }
            }
          : undefined
      }
      className={cx(
        transcripts !== null &&
          'hover:bg-bg-secondary focus-visible:outline-border-brand cursor-pointer focus-visible:outline focus-visible:outline-2'
      )}
    >
      <Table.Cell mono title={outcome.because}>
        {transcripts !== null && (
          <span className="text-fg-brand-primary mr-1" aria-hidden>
            ▸
          </span>
        )}
        {outcome.scenarioId}
      </Table.Cell>
      <Table.Cell className={DECISION_TONE_CLASS[outcome.decision]}>{outcome.decision}</Table.Cell>
      <Table.Cell mono className={DROP_TONE_CLASS[dropTone(outcome.drop)]}>
        {formatDrop(outcome.drop)}
      </Table.Cell>
      <Table.Cell mono muted>
        {outcome.base.passCount}/{outcome.base.okCount}
      </Table.Cell>
      <Table.Cell mono muted>
        {outcome.head.passCount}/{outcome.head.okCount}
      </Table.Cell>
      <Table.Cell
        mono
        muted={tokenChange === null}
        className={cx(
          // A step down from the table's `text-sm`: the release page gives this
          // panel a fixed width, and every pixel this column takes there is one
          // the scenario id loses.
          'whitespace-nowrap text-xs',
          tokenChange !== null && 'text-fg-warning-primary'
        )}
        title={tokenChange?.detail}
      >
        {tokenChange === null ? '—' : tokenChange.parts.map((part) => <div key={part}>{part}</div>)}
      </Table.Cell>
    </Table.Row>
  );
};

interface IBenchmarkVerdictViewProps {
  verdict: IMopsAssistantBenchmarkArtifactV1;
}

/**
 * One run's verdict: the headline, the per-scenario outcomes table (rows open the
 * base-vs-head transcript modal when a scenario carries a capture), and the
 * static prompt-budget section.
 */
const BenchmarkVerdictView = ({ verdict }: IBenchmarkVerdictViewProps) => {
  const { overall, plan, outcomes } = verdict;
  const [transcriptScenario, setTranscriptScenario] = React.useState<ITranscriptScenario | null>(null);
  // Unchanged scenarios are the bulk of a healthy run and carry no signal, so the
  // table opens on the movers alone and keeps the rest one click away.
  const [showUnchanged, setShowUnchanged] = React.useState(false);
  const [sort, setSort] = React.useState<IBenchmarkSort>(DEFAULT_BENCHMARK_SORT);
  // Clicking the column already in effect flips it; clicking another takes over
  // at that column's own worst-first direction.
  const sortBy = (column: BenchmarkSortColumn) =>
    setSort((current) =>
      current.column === column
        ? { column, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: defaultSortDirection(column) }
    );
  const { notable, unchanged } = React.useMemo(() => partitionBenchmarkOutcomes(outcomes, sort), [outcomes, sort]);
  // Headline precedence, high to low: inconclusive (no verdict) > advisory scorer
  // drift > confirmed regression > clean — the tone class below follows it, as does
  // the CI summary. (The terminal status ranks a regression first, so a rare
  // regression+inconclusive run reads `failed` there but leads with the caveat here.)
  const headline =
    overall.inconclusiveReason !== null
      ? `Inconclusive — ${overall.inconclusiveReason}`
      : overall.scorerDrift != null
        ? `Advisory — scoring changed (${overall.scorerDrift}); verdicts may reflect the scorer, not behavior`
        : overall.behaviorRegressed
          ? 'Behavioral regression detected'
          : 'No behavioral regression';

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <span
          className={cx(
            'text-xs font-medium',
            overall.inconclusiveReason !== null || overall.scorerDrift != null
              ? 'text-fg-warning-primary'
              : overall.behaviorRegressed
                ? 'text-fg-error-primary'
                : 'text-fg-success-primary'
          )}
        >
          {headline}
        </span>
        <span className="text-fg-tertiary text-[11px]">
          Compared {plan.gatedCount} of {plan.matchedCount} scenarios
          {plan.changed.length > 0 && ` · ${plan.changed.length} changed`}
          {plan.skipped.length > 0 && ` · ${plan.skipped.length} skipped`}
          {overall.perfRegressed && ' · perf regressed (advisory)'}
        </span>
      </div>

      {outcomes.length > 0 && (
        <Table>
          <Table.Header>
            <Table.Row>
              {VERDICT_COLUMNS.map(({ column, label, title, description }) => (
                <Table.SortableHeaderCell
                  key={column}
                  sortDirection={sort.column === column ? sort.direction : null}
                  onSort={() => sortBy(column)}
                  tooltip={{ title, description }}
                >
                  {label}
                </Table.SortableHeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {notable.map((outcome) => (
              <OutcomeRow key={outcome.scenarioId} outcome={outcome} onOpen={setTranscriptScenario} />
            ))}
            {unchanged.length > 0 && (
              <Table.Row>
                <Table.Cell colSpan={VERDICT_COLUMNS.length} className="py-1">
                  <Button
                    color="secondary"
                    size="xs"
                    onClick={() => setShowUnchanged((shown) => !shown)}
                    aria-expanded={showUnchanged}
                  >
                    <span aria-hidden>{showUnchanged ? '▾' : '▸'}</span> {unchanged.length} unchanged
                    {unchanged.length === 1 ? ' scenario' : ' scenarios'}
                  </Button>
                </Table.Cell>
              </Table.Row>
            )}
            {showUnchanged &&
              unchanged.map((outcome) => (
                <OutcomeRow key={outcome.scenarioId} outcome={outcome} onOpen={setTranscriptScenario} />
              ))}
          </Table.Body>
        </Table>
      )}

      {/* Measured on every run — including inconclusive bails — so it shows even
          when there's no behavioral verdict to compare. */}
      <PromptBudgetView promptBudget={verdict.promptBudget ?? null} />

      {transcriptScenario !== null && (
        <BenchmarkTranscriptModal
          scenarioId={transcriptScenario.scenarioId}
          transcripts={transcriptScenario.transcripts}
          isOpen
          onClose={() => setTranscriptScenario(null)}
        />
      )}
    </div>
  );
};

export default BenchmarkVerdictView;
